import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  CheckmarkFilled,
  CloseFilled,
  WarningFilled,
  InformationFilled,
  Close,
} from "@carbon/icons-react";
import { CToast } from "@coreui/react";
import { CSpinner } from "@coreui/react";
import { TOAST_TYPES } from "src/constants/constants";

import CopyToClipboard from "src/components/UtilityComponents/copyToClipboard/CopyToClipboard";

import "./ToastStyle.css";
import "src/scss/_icons.scss";

const Toast = ({
  type = TOAST_TYPES.INFO,
  title,
  content,
  delay, // When autohide is true, this is the time delay before it disappears (in milliseconds)
  copy = false, // Whether to show the copy to clipboard icon
  autohide = true, // Whether to automatically hide the toast after the delay
}) => {
  const [visible, setVisible] = useState(true);

  // Only use delay if autohide is true, otherwise it's meaningless
  const effectiveDelay = autohide ? (delay ?? 10000) : undefined;

  const toastConfig = {
    [TOAST_TYPES.SUCCESS]: {
      backgroundColor: "var(--success-light-1)",
      color: "var(--success-dark-1)",
      Icon: CheckmarkFilled,
    },
    [TOAST_TYPES.ERROR]: {
      backgroundColor: "var(--danger-light-1)",
      color: "var(--danger-dark-1)",
      Icon: CloseFilled,
    },
    [TOAST_TYPES.WARNING]: {
      backgroundColor: "var(--warning-light-1)",
      color: "var(--warning-dark-1)",
      Icon: WarningFilled,
    },
    [TOAST_TYPES.INFO]: {
      backgroundColor: "var(--info-light-1)",
      color: "var(--info-dark-1)",
      Icon: InformationFilled,
    },
    [TOAST_TYPES.DOWNLOAD]: {
      backgroundColor: "var(--info-light-1)",
      color: "var(--info-dark-1)",
      Icon: InformationFilled,
    },
  };

  const { backgroundColor, color, Icon } =
    toastConfig[type] || toastConfig[TOAST_TYPES.INFO];

  const handleClose = () => {
    setVisible(false);
  };

  const renderIcon = () => {
    if (type === TOAST_TYPES.DOWNLOAD) {
      return <CSpinner size="sm" className="custom-toast-icon mt-1" />;
    }
    return Icon ? (
      <Icon className="medium-icon custom-toast-icon mt-1" style={{ color }} />
    ) : null;
  };

  return (
    <CToast
      visible={visible}
      autohide={autohide}
      delay={effectiveDelay}
      style={{
        backgroundColor,
        borderLeft: `5px solid ${color}`,
        position: "fixed",
        right: 0,
        top: 0,
      }}
      className="custom-toast-container"
    >
      <div className="d-flex custom-toast-body" style={{ color }}>
        <div className="d-flex align-items-start">
          {renderIcon()}
          <div>
            <div className="custom-toast-title">{title}</div>
            <span className="custom-toast-content">{content} </span>
          </div>
        </div>
        <div className="custom-toast-icons">
          {copy && <CopyToClipboard text={content} style={{ color }} />}
          <Close onClick={handleClose} className="medium-icon clickable-icon" />
        </div>
      </div>
    </CToast>
  );
};

Toast.propTypes = {
  type: PropTypes.oneOf(Object.values(TOAST_TYPES)),
  title: PropTypes.string,
  content: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  delay: PropTypes.number,
  copy: PropTypes.bool,
  autohide: PropTypes.bool,
};

export default Toast;
